import {useEffect, useRef} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {ProfileComparisonDevelopmentEffortTile} from './ProfileComparisonDevelopmentEffortTile';
import {ProfileComparisonGapBreakdown} from './ProfileComparisonGapBreakdown';
import {ProfileComparisonResultScoreCards} from './ProfileComparisonResultScoreCards';
import {ProfileComparisonResultSkeleton} from './ProfileComparisonResultSkeleton';
import {ProfileComparisonRoleMismatchWarning} from './ProfileComparisonRoleMismatchWarning';
import {
  profileComparisonResultEffortGridSx,
  profileComparisonResultSectionSx,
  profileComparisonSectionCardSx,
} from './profileComparisonSectionStyles';
import {ProfileComparisonVerdictCallout} from './ProfileComparisonVerdictCallout';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {useProfileComparisonResult} from '../../hooks/useProfileComparisonResult';

export interface Props {
  companyId: string;
  profileAPublicId: string | null;
  profileBPublicId: string | null;
}

export const ProfileComparisonResult = ({companyId, profileAPublicId, profileBPublicId}: Props) => {
  const {t} = useTranslation('translation', {
    keyPrefix: 'workforceDepartmentHealthPage.profileComparison.result',
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const scrolledPairRef = useRef<string | null>(null);

  const {data, isLoading, error} = useProfileComparisonResult(companyId, profileAPublicId, profileBPublicId);

  const pairKey =
    profileAPublicId && profileBPublicId ? `${profileAPublicId}:${profileBPublicId}` : null;

  useEffect(() => {
    if (!data || isLoading || !pairKey || scrolledPairRef.current === pairKey) {
      return;
    }

    scrolledPairRef.current = pairKey;
    containerRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
  }, [data, isLoading, pairKey]);

  if (!profileAPublicId || !profileBPublicId) {
    return (
      <Box sx={profileComparisonResultSectionSx} data-testid='profile-comparison-result'>
        <EmpionPaper sx={profileComparisonSectionCardSx}>
          <Typography variant='body2' color='text.secondary'>
            {t('missingPublicId')}
          </Typography>
        </EmpionPaper>
      </Box>
    );
  }

  if (isLoading) {
    return <ProfileComparisonResultSkeleton />;
  }

  if (error || !data) {
    return (
      <Box ref={containerRef} sx={profileComparisonResultSectionSx} data-testid='profile-comparison-result'>
        <EmpionPaper sx={profileComparisonSectionCardSx}>
          <Typography variant='body2' color='error'>
            {t('loadingError')}
          </Typography>
        </EmpionPaper>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={profileComparisonResultSectionSx} data-testid='profile-comparison-result'>
      <EmpionPaper sx={{...profileComparisonSectionCardSx, mb: 0}}>
        <Typography sx={{fontSize: '16px', fontWeight: 600, mb: 2}}>{t('title')}</Typography>

        {data.roleMismatch && <ProfileComparisonRoleMismatchWarning />}

        <ProfileComparisonVerdictCallout verdict={data.verdict} profileAPublicId={data.profileA.profileId} />

        <ProfileComparisonResultScoreCards
          profileA={data.profileA}
          profileB={data.profileB}
          dimensionDeltas={data.dimensionDeltas}
        />

        <ProfileComparisonGapBreakdown gapBreakdown={data.gapBreakdown} />

        <Typography sx={{fontSize: '13px', fontWeight: 600, mb: 1.25}}>{t('developmentEffortTitle')}</Typography>
        <Box sx={profileComparisonResultEffortGridSx}>
          <ProfileComparisonDevelopmentEffortTile slot='a' effort={data.developmentEffort.profileA} />
          <ProfileComparisonDevelopmentEffortTile slot='b' effort={data.developmentEffort.profileB} />
        </Box>
      </EmpionPaper>
    </Box>
  );
};
